import React, { useState } from 'react';
import { useWebhook } from '../hooks/useWebhook';
import { toast } from 'react-hot-toast';

interface JsonInputFormProps {
  webhookType?: 'contact' | 'analytics' | 'batch';
  eventName?: string;
  onSubmit?: (data: any) => void;
  onSuccess?: (response: any) => void;
  onError?: (error: Error) => void;
  className?: string;
  submitButtonText?: string;
  loadingText?: string;
  placeholder?: string;
  defaultJson?: string;
}

export const JsonInputForm: React.FC<JsonInputFormProps> = ({
  webhookType: initialWebhookType = 'analytics',
  eventName: initialEventName = 'custom_event',
  onSubmit,
  onSuccess,
  onError,
  className = '',
  submitButtonText = '전송하기',
  loadingText = '전송 중...',
  placeholder = 'JSON 데이터를 입력하세요...',
  defaultJson = '{\n  "key": "value",\n  "number": 123,\n  "array": [1, 2, 3],\n  "object": {\n    "nested": "data"\n  }\n}'
}) => {
  const [jsonData, setJsonData] = useState(defaultJson);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [eventName, setEventName] = useState(initialEventName);
  const [webhookType, setWebhookType] = useState(initialWebhookType);

  const { sendCustomEvent, isLoading, error } = useWebhook({
    webhookType,
    onSuccess,
    onError,
  });

  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      setJsonError(null);
      return true;
    } catch (err) {
      setJsonError('올바른 JSON 형식이 아닙니다.');
      return false;
    }
  };

  const handleJsonChange = (value: string) => {
    setJsonData(value);
    if (value.trim()) {
      validateJson(value);
    } else {
      setJsonError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateJson(jsonData)) {
      toast.error('JSON 형식을 확인해주세요.');
      return;
    }

    try {
      const parsedData = JSON.parse(jsonData);
      const response = await sendCustomEvent(eventName, parsedData, webhookType);
      
      if (response.success) {
        toast.success('데이터가 성공적으로 전송되었습니다!');
        onSubmit?.(parsedData);
      }
    } catch (error) {
      console.error('JSON 전송 실패:', error);
      toast.error('전송 중 오류가 발생했습니다.');
    }
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(jsonData);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonData(formatted);
      setJsonError(null);
      toast.success('JSON이 포맷되었습니다.');
    } catch (err) {
      toast.error('JSON 형식이 올바르지 않습니다.');
    }
  };

  const handleClear = () => {
    setJsonData('');
    setJsonError(null);
  };

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            JSON 데이터 *
          </label>
          <div className="relative">
            <textarea
              value={jsonData}
              onChange={(e) => handleJsonChange(e.target.value)}
              placeholder={placeholder}
              rows={12}
              className={`w-full font-mono text-sm rounded-md border ${
                jsonError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              } shadow-sm p-3`}
            />
            {jsonError && (
              <p className="mt-1 text-sm text-red-600">{jsonError}</p>
            )}
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            type="button"
            onClick={handleFormatJson}
            className="px-3 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded"
          >
            JSON 포맷
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="px-3 py-2 text-sm bg-gray-300 hover:bg-gray-400 text-gray-700 rounded"
          >
            지우기
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              이벤트 이름
            </label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="event_name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              웹훅 타입
            </label>
            <select
              value={webhookType}
              onChange={(e) => setWebhookType(e.target.value as 'contact' | 'analytics' | 'batch')}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="analytics">Analytics</option>
              <option value="contact">Contact</option>
              <option value="batch">Batch</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
            {error.message}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || !!jsonError}
          className="w-full bg-blue-500 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-2 px-4 rounded"
        >
          {isLoading ? loadingText : submitButtonText}
        </button>
      </div>
    </form>
  );
}; 